"use client"

import React from "react"
import type { UseFormReturn } from "react-hook-form"
import { FormField, FormItem, FormControl, FormMessage, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from "@/components/ui/components"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/components/tooltip"
import { Info } from "lucide-react"

export default function ReclamationFields(props: {
  form: UseFormReturn<any>
  t: (k: string) => string
  show: (k: string) => boolean
  // required flags
  reqReclArticle: boolean
  reqReclQuantity: boolean
  reqReclReason: boolean
  reqReclDamage: boolean
  reqReclMessage: boolean
  reqReclHandling: boolean
  // labels
  reclamationLabel: string
  article?: string
  quantity?: string
  reasonForComplaint?: string
  damage?: string
  damageYes?: string
  damageNo?: string
  reclamationMessage?: string
  handling?: string
  handlingInfo?: string
  // options
  reclamationReasonOptions: string[]
  handlingOptions: string[]
  // styles
  formItemClass: string
  formLabelClass: string
  formLabelWithIconClass: string
  textFieldStyle: string
  textFieldStyleMultiline: string
  radioButtonStyle: string
}) {
  return (
    <>
      <div className="mt-4">
        <p className="mb-2 font-semibold">{props.reclamationLabel}</p>
      </div>

      {props.show("article_active") && (
        <FormField
          control={props.form.control}
          name="ReclamationForm_ReclamationArticle"
          render={({ field }) => (
            <FormItem className={props.formItemClass}>
              <label className={props.formLabelClass}>
                {props.article ?? "Betroffener Artikel"}
                {props.reqReclArticle && <span className="ml-1 text-sm text-primary">*</span>}
              </label>
              <FormControl>
                <Input className={props.textFieldStyle} {...field} placeholder={props.article ?? "Betroffener Artikel"} />
              </FormControl>
              <FormMessage className="lg:col-start-2" />
            </FormItem>
          )}
        />
      )}

      {props.show("quantity_active") && (
        <FormField
          control={props.form.control}
          name="ReclamationForm_ReclamationQuantity"
          render={({ field }) => (
            <FormItem className={props.formItemClass}>
              <label className={props.formLabelClass}>
                {props.quantity ?? "Menge"}
                {props.reqReclQuantity && <span className="ml-1 text-sm text-primary">*</span>}
              </label>
              <FormControl>
                <Input className={props.textFieldStyle} {...field} placeholder={props.quantity ?? "Menge"} />
              </FormControl>
              <FormMessage className="lg:col-start-2" />
            </FormItem>
          )}
        />
      )}

      {props.show("reason_for_complaint_active") && (
        <FormField
          control={props.form.control}
          name="ReclamationForm_ReclamationReason"
          render={({ field }) => (
            <FormItem className={props.formItemClass}>
              <label className={props.formLabelClass}>
                {props.reasonForComplaint ?? "Reklamationsgrund"}
                {props.reqReclReason && <span className="ml-1 text-sm text-primary">*</span>}
              </label>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className={props.textFieldStyle}>
                    <SelectValue placeholder={props.t("common.filter_select")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {props.reclamationReasonOptions.length > 0 ? (
                    props.reclamationReasonOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="" disabled>
                      {props.t("common.filter_select")}
                    </SelectItem>
                  )}
                </SelectContent>
                <input type="hidden" name={field.name} value={field.value ?? ""} />
              </Select>
              <FormMessage className="lg:col-start-2" />
            </FormItem>
          )}
        />
      )}

      {props.show("damage_active") && (
        <FormField
          control={props.form.control}
          name="ReclamationForm_ReclamationDamage"
          render={({ field }) => (
            <FormItem className={props.formItemClass}>
              <label className={props.formLabelClass}>
                {props.damage ?? "Schaden bemerkt"}
                {props.reqReclDamage && <span className="ml-1 text-sm text-primary">*</span>}
              </label>
              <FormControl>
                <div className="flex flex-row gap-6">
                  <div className="flex items-center gap-2">
                    <input className={props.radioButtonStyle} id="damageYes" type="radio" value="JA" checked={field.value === "JA"} onChange={() => field.onChange("JA")} />
                    <label htmlFor="damageYes">{props.damageYes ?? "ja"}</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input className={props.radioButtonStyle} id="damageNo" type="radio" value="NEIN" checked={field.value === "NEIN"} onChange={() => field.onChange("NEIN")} />
                    <label htmlFor="damageNo">{props.damageNo ?? "nein"}</label>
                  </div>
                </div>
              </FormControl>
              <FormMessage className="lg:col-start-2" />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={props.form.control}
        name="ReclamationForm_ReclamationMessage"
        render={({ field }) => (
          <FormItem className={props.formItemClass}>
            <label className={props.formLabelClass}>
              {props.reclamationMessage ?? "Nachricht"}
              {props.reqReclMessage && <span className="ml-1 text-sm text-primary">*</span>}
            </label>
            <FormControl>
              <Textarea className={props.textFieldStyleMultiline + " h-32"} {...field} placeholder={props.reclamationMessage ?? "Nachricht"} />
            </FormControl>
            <FormMessage className="lg:col-start-2" />
          </FormItem>
        )}
      />

      {props.show("handling_active") && (
        <FormField
          control={props.form.control}
          name="ReclamationForm_ReclamationHandling"
          render={({ field }) => (
            <FormItem className={props.formItemClass}>
              <label className={props.formLabelWithIconClass}>
                <span>{props.handling ?? "Abwicklungswunsch"}</span>
                {props.handlingInfo && (
                  <TooltipProvider delayDuration={50} skipDelayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-fit cursor-pointer">
                          <Info className="size-4 text-gray-400" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-sm" sideOffset={4}>
                        {props.handlingInfo}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {props.reqReclHandling && <span className="text-sm text-primary">*</span>}
              </label>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className={props.textFieldStyle}>
                    <SelectValue placeholder={props.t("common.filter_select")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {props.handlingOptions.length > 0 ? (
                    props.handlingOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value="" disabled>
                      {props.t("common.filter_select")}
                    </SelectItem>
                  )}
                </SelectContent>
                <input type="hidden" name={field.name} value={field.value ?? ""} />
              </Select>
              <FormMessage className="lg:col-start-2" />
            </FormItem>
          )}
        />
      )}
    </>
  )
}
